import {IncomingMessage} from 'http'

import {QueryClient, QueryClientConfig} from '@tanstack/react-query'
import {isAxiosError} from 'axios'

import {INTERNAL_ROUTES} from '$constants/internal-routes'
import {sendNeloToBFF} from '$services/nelo'
import {BASE_PATH} from '$share/config'
import {NeloLogLevel} from '$share/constants/nelo'
import {isSSR} from '$utils/common'

import {catchAxiosErrorOnTanstackQuery} from './throwOnError'

const handleThrowOnError = (error: Error) => {
    if (isSSR()) {
        return true
    }

    if (isAxiosError(error)) {
        const result = catchAxiosErrorOnTanstackQuery(error, {
            currentUrl: window.location.href,
            userAgent: window.navigator.userAgent,
            redirect(redirectUrl) {
                window.location.href = redirectUrl
                return false
            },
        })

        return !!result
    }

    return true
}

const sendErrorToNelo = (error: unknown, logType: string, req?: IncomingMessage) => {
    if (error instanceof Error && !isAxiosError(error)) {
        sendNeloToBFF(
            {
                logLevel: NeloLogLevel.Error,
                logType,
                log: {
                    name: error.name,
                    message: error.message,
                    pageUrl: isSSR() ? req?.url : window.location.href,
                    ...(error?.stack ? {stack: error.stack} : {}),
                },
            },
            req,
        )
    }
}

const refetchOnWindowFocusOnHome = () => {
    const {pathname} = new URL(window.location.href)

    if (pathname === `${BASE_PATH}${INTERNAL_ROUTES.INSURANCE_MY_HOME}`) {
        return 'always'
    }

    return false
}

const insuranceQueryClientConfig = (req?: IncomingMessage): QueryClientConfig => {
    return {
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: !isSSR() && refetchOnWindowFocusOnHome(),
                refetchOnMount: false,
                staleTime: Infinity,
                retry: false,
                retryOnMount: false,
                throwOnError: handleThrowOnError,
            },
            mutations: {
                onError: (error: unknown) => {
                    sendErrorToNelo(error, 'react-query-mutation', req)
                },
                throwOnError: handleThrowOnError,
            },
        },
    }
}

export const createInsuranceQueryClient = (req?: IncomingMessage) => new QueryClient(insuranceQueryClientConfig(req))
